import { Typography } from '@euler/app/components/typography';
import { Button } from '@euler/components/Button';
import { Colors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { dateFromValue } from '@euler/utils/datetime';
import { formatDate, formatDateTime } from '@euler/utils/formatters';
import { useCreation } from '@euler/utils/hooks';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DateTimePicker = (props: {
  isVisible: boolean;
  date?: Date;
  minimumDate?: Date;
  title?: string;
  mode?: 'time' | 'date' | 'datetime' | undefined;
  onConfirm?: (newDate: Date | undefined) => void;
  onCancel?: () => void;
}) => {
  const { rpx } = useResponsiveContext();
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(() => {
    const date = props.date;
    if (date == null) return '';
    if (props.mode === 'datetime') {
      return formatDateTime(date, 'yyyy-MM-dd HH:mm:ss');
    }
    if (props.mode === 'date') {
      return formatDate(date, 'yyyy-MM-dd');
    }
    if (props.mode === 'time') {
      return formatDate(date, 'HH:mm:ss');
    }
    return date.toString();
  });
  useEffect(() => {
    if (props.isVisible) {
      ref.current?.click();
    }
  }, [props.isVisible]);
  const dom = useCreation(() => document.createElement('div'), []);
  useEffect(() => {
    document.body.appendChild(dom);
    return () => void document.body.removeChild(dom);
  }, [dom]);
  if (!props.isVisible) {
    return null;
  }
  return createPortal(
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: rpx(300),
          padding: rpx(24),
          borderRadius: rpx(10),
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
        }}
      >
        <Typography.Title
          level={1}
          align="center"
          family="medium"
          size={rpx(21)}
        >
          {props.title ?? '请选择日期/时间'}
        </Typography.Title>
        <input
          ref={ref}
          value={value}
          onChange={e => {
            setValue(e.currentTarget.value);
          }}
          min={props.minimumDate?.toISOString()}
          type={
            props.mode === 'date'
              ? 'date'
              : props.mode === 'time'
              ? 'time'
              : 'datetime-local'
          }
          style={{
            padding: rpx(10),
            borderWidth: 1,
            borderRadius: rpx(5),
            marginTop: rpx(15),
            marginBottom: rpx(15),
          }}
        />
        <Button
          style={{ marginTop: rpx(10) }}
          title="确定"
          textStyle={{
            color: 'white',
            fontFamily: FontFamily.NotoSans.Medium,
            fontSize: rpx(16),
          }}
          css={`
            background-color: rgb(32, 127, 231);
          `}
          onPress={() => {
            props.onConfirm?.(
              props.mode === 'time'
                ? dateAtClock(props.date, value)
                : dateFromValue(value),
            );
          }}
        />
        <Button
          style={{ marginTop: rpx(10) }}
          title="取消"
          onPress={props.onCancel}
          textStyle={{
            color: '#333',
            fontFamily: FontFamily.NotoSans.Medium,
            fontSize: rpx(16),
          }}
          css={`
            background-color: ${Colors.Gray4};
          `}
        />
      </div>
    </div>,
    dom,
  );
};

/** A time input yields only `HH:mm[:ss]`, which `Date` cannot parse. */
function dateAtClock(date: Date | undefined, clock: string): Date {
  const [hours, minutes, seconds] = clock.split(':').map(Number);
  const result = new Date(date ?? Date.now());
  result.setHours(hours || 0, minutes || 0, seconds || 0, 0);
  return result;
}

export default DateTimePicker;
